import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { ProgressSpinner } from 'primereact/progressspinner';
import React, { useMemo } from 'react';
import { commaSeparatedNumbers } from '../../../../utils/commaSeparatedNumbers';
import classes from './hourlybphByRoom.module.scss';
import { RoomData } from './hourlybphByRoom.type';

interface HourlyBPHByRoomScreenProps {
  hourlyBPHByRoomData: RoomData[];
  loading: boolean;
}

interface TableRow {
  id: string;
  type: 'room' | 'job';
  room_name: string;
  [key: string]: string | number;
}

// Body renderer functions defined outside component
const renderRoomJobName = (rowData: TableRow) => (
  <div className={rowData.type === 'room' ? classes.roomName : classes.jobName}>
    {rowData.room_name}
  </div>
);

const renderTimeSlotValue = (slot: string) => (rowData: TableRow) => (
  <div className={rowData.type === 'room' ? classes.roomValue : classes.jobValue}>
    {commaSeparatedNumbers(rowData[slot])}
  </div>
);

const HourlyBPHByRoom: React.FC<HourlyBPHByRoomScreenProps> = ({
  hourlyBPHByRoomData,
  loading
}) => {
  // Get all unique time slots from the first data entry (assuming all have the same slots)
  const timeSlots = useMemo(() => {
    if (hourlyBPHByRoomData.length === 0) {
      return [];
    }
    return Object.keys(hourlyBPHByRoomData[0].total_bph);
  }, [hourlyBPHByRoomData]);

  // Prepare table data: create rows for both room totals and job details
  const tableData = useMemo(() => {
    const rows: TableRow[] = [];

    hourlyBPHByRoomData.forEach(room => {
      // Add room total row
      rows.push({
        id: `room-${room.room_name}`,
        type: 'room',
        room_name: room.room_name,
        ...room.total_bph
      });

      // Add job rows for this room
      Object.entries(room.hourly_jobs).forEach(([jobId, jobData]) => {
        rows.push({
          id: `job-${room.room_name}-${jobId}`,
          type: 'job',
          room_name: jobId,
          ...jobData
        });
      });
    });

    return rows;
  }, [hourlyBPHByRoomData]);

  if (loading === true) {
    return (
      <div className={classes.loadingContainer}>
        <ProgressSpinner className={classes.spinner} />
        <div>Loading Hourly BPH By Room data...</div>
      </div>
    );
  }

  return (
    <div className={classes.hourlyBPHByRoomContainer}>
      <DataTable
        value={tableData}
        emptyMessage="No records found"
        className={classes.dataTable}
        rowClassName={rowData => (rowData.type === 'room' ? classes.roomRow : classes.jobRow)}
        resizableColumns
        scrollable
        scrollHeight="flex"
        loading={loading}
        columnResizeMode="expand"
      >
        <Column
          field="room_name"
          header="Room/Job"
          frozen
          bodyClassName={rowData =>
            rowData.type === 'room' ? classes.stickyColumnRoom : classes.stickyColumnJob
          }
          body={renderRoomJobName}
        />
        {timeSlots.map(slot => (
          <Column
            key={slot}
            field={slot}
            header={slot}
            bodyClassName={classes.columndata}
            body={renderTimeSlotValue(slot)}
          />
        ))}
      </DataTable>
    </div>
  );
};

export default HourlyBPHByRoom;
